import React, { useState } from 'react';
import { MessageSquareWarning, Zap, ChevronDown, ChevronRight, XCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { GlassCard } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { analyzePhishingLinguistics } from '@/lib/phishingLab';
import { useApp } from '@/context/AppContext';

const exampleText = `Dear Valued Customer,

URGENT: Your account has been suspended due to suspicious activity!

This is an official notice from the Security Team. You must verify your identity immediately or your account will be closed within 24 hours.

Please confirm your password and enter your social security number at the link below. Failure to respond will result in legal action.

Click here: http://g00gle-login.xyz/verify?id=12345

Congratulations! You've also been selected as a prize winner. Claim your reward NOW before it's too late!

Best regards,
IT Department`;

const FeatureBar = ({ feature }) => {
  const [expanded, setExpanded] = useState(false);
  const pct = feature.maxScore > 0 ? (feature.score / feature.maxScore) * 100 : 0;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between cursor-pointer group" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                    : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />}
          <span className="text-xs sm:text-sm font-medium truncate">{feature.name}</span>
          {feature.matchCount > 0 && <span className="text-[10px] text-destructive font-mono">{feature.matchCount} hit(s)</span>}
        </div>
        <span className="text-xs font-mono flex-shrink-0 ml-2">
          <span className={cn("font-bold", feature.score > 0 ? 'text-destructive' : 'text-success')}>{feature.score}</span>
          <span className="text-muted-foreground">/{feature.maxScore}</span>
        </span>
      </div>
      <div className="relative h-2 w-full overflow-hidden rounded-full bg-muted">
        <div className={cn("h-full transition-all duration-500 ease-out rounded-full",
          pct >= 70 ? 'bg-destructive' : pct >= 40 ? 'bg-warning' : pct > 0 ? 'bg-orange-400' : 'bg-success')}
          style={{ width: `${pct}%` }} />
      </div>
      {expanded && feature.matches.length > 0 && (
        <div className="ml-5 mt-1 space-y-1">
          {feature.matches.map((m, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px]">
              <XCircle className="w-3 h-3 text-destructive flex-shrink-0" />
              <span className="text-muted-foreground">"{m.text}" <span className="font-mono text-destructive">(+{m.weight})</span></span>
            </div>
          ))}
        </div>
      )}
      {expanded && feature.matches.length === 0 && (
        <div className="ml-5 mt-1 flex items-center gap-2 text-[10px]">
          <CheckCircle2 className="w-3 h-3 text-success flex-shrink-0" />
          <span className="text-muted-foreground">No indicators detected</span>
        </div>
      )}
    </div>
  );
};

export const LinguisticsLab = () => {
  const [text, setText] = useState('');
  const [result, setResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { addVerification } = useApp();

  const handleAnalyze = async () => {
    if (text.trim().length < 20) return;
    setIsLoading(true);
    await new Promise(r => setTimeout(r, 500));
    const analysis = analyzePhishingLinguistics(text);
    setResult(analysis);

    addVerification({
      type: 'phishing-lab',
      input: `Linguistics · ${text.substring(0, 80)}${text.length > 80 ? '…' : ''}`,
      verdict: 'Lab Analysis',
      score: analysis?.totalScore || 0,
      result: {
        mode: 'linguistics',
        totalScore: analysis?.totalScore || 0,
      },
    });

    setIsLoading(false);
  };

  return (
    <div className="space-y-6">
      <GlassCard className="p-4 sm:p-5">
        <div className="space-y-3">
          <Textarea placeholder="Paste email body text to analyze for phishing linguistics..." value={text}
            onChange={e => setText(e.target.value)} className="min-h-[140px] text-sm resize-none" data-testid="linguistics-input" />
          <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
            <Button onClick={handleAnalyze} disabled={text.trim().length < 20 || isLoading} variant="gradient"
              className="w-full sm:w-auto h-11" data-testid="linguistics-analyze-btn">
              {isLoading ? <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                : <><MessageSquareWarning className="w-4 h-4 mr-2" />Analyze Linguistics</>}
            </Button>
            <button onClick={() => setText(exampleText)} data-testid="linguistics-example-btn"
              className="text-xs text-muted-foreground hover:text-foreground transition-colors underline underline-offset-2">
              Load phishing example
            </button>
          </div>
        </div>
      </GlassCard>

      {result && (
        <div className="space-y-4">
          <GlassCard className={cn("p-4 sm:p-5 border-2",
            result.riskScore >= 60 ? 'border-destructive/30' : result.riskScore >= 30 ? 'border-warning/30' : 'border-success/30')}>
            <div className="flex items-center gap-4">
              <div className={cn("w-16 h-16 rounded-full flex items-center justify-center border-4",
                result.riskScore >= 60 ? 'border-destructive/50 bg-destructive/10' :
                result.riskScore >= 30 ? 'border-warning/50 bg-warning/10' : 'border-success/50 bg-success/10')}>
                <span className={cn("text-xl font-bold",
                  result.riskScore >= 60 ? 'text-destructive' : result.riskScore >= 30 ? 'text-warning' : 'text-success')}>
                  {result.riskScore}
                </span>
              </div>
              <div>
                <Badge variant={result.riskScore >= 60 ? 'danger' : result.riskScore >= 30 ? 'suspicious' : 'safe'} className="text-sm">
                  {result.verdict}
                </Badge>
                <p className="text-xs text-muted-foreground mt-1">
                  {result.totalMatches} linguistic indicators across {result.features.length} categories
                </p>
              </div>
            </div>
          </GlassCard>

          <GlassCard className="p-4 sm:p-5" data-testid="linguistics-breakdown">
            <h4 className="text-sm font-semibold mb-4 flex items-center gap-2">
              <Zap className="w-4 h-4 text-primary" />Detection Breakdown
            </h4>
            <div className="space-y-4">
              {result.features.map(feat => <FeatureBar key={feat.key} feature={feat} />)}
            </div>
          </GlassCard>
        </div>
      )}
    </div>
  );
};
